import io from "socket.io-client";
import SimplePeer from "simple-peer";

import api from "@/api";

import { INTERVIEW_VIEW } from "../interview.const";
import { initInterviewServerState, initInterviewServerState__Peer } from "./interview-server.inits";

/** @type {(import("vuex").Module<import("./interview-server.inits").InterviewServerState>)} */
export const server = {
    namespaced: true,

    state: initInterviewServerState(),

    getters: {
        peers(state, getters, rootState, rootGetters) {
            return Object.values(state.peers);
        },
        dataString(state, getters, rootState, rootGetters) {
            return JSON.stringify({
                enabled_audio: rootState.interview.client.enabled_audio,
                enabled_video: rootState.interview.client.enabled_video,
            });
        },
        interviewCode(state, getters, rootState, rootGetters) {
            return rootState.interview.code;
        },
    },

    mutations: {
        pushSeeker(state, { seeker }) {
            let index = state.seekers.findIndex(({ _id }) => _id == seeker._id);
            if (-1 < index) state.seekers.splice(index, 1, seeker);
            else state.seekers.push(seeker);
            state.seekers = [...(state.seekers || [])];
        },
        pullSeeker(state, { seeker }) {
            let index = state.seekers.findIndex(({ _id }) => _id == seeker._id);
            if (-1 < index) state.seekers.splice(index, 1);
            state.seekers = [...(state.seekers || [])];
        },
    },

    actions: {
        async init({ state, rootState, dispatch }) {
            if (!state.socket) {
                const { accessToken } = rootState;

                /** @type {Partial<import("socket.io-client").ManagerOptions & import("socket.io-client").SocketOptions>} */
                let options = {
                    path: "/socket",
                    transports: ["websocket"],
                    auth: { accessToken },
                };

                if (!accessToken) delete options.auth.accessToken;

                state.socket = io("/interview", options);
                state.socket.on("authed", (payload) => dispatch("onAuthed", payload));
                state.socket.on("closed", (payload) => dispatch("onClosed", payload));
                state.socket.on("joined", (payload) => dispatch("onJoined", payload));
                state.socket.on("seeked", (payload) => dispatch("onSeeked", payload));
                state.socket.on("signal", (payload) => dispatch("onSignal", payload));
                state.socket.on("room-closed", (payload) => dispatch("onRoomClosed", payload));
                state.socket.on("user-authed", (payload) => dispatch("onUserAuthed", payload));
                state.socket.on("user-exited", (payload) => dispatch("onUserExited", payload));
                state.socket.on("user-joined", (payload) => dispatch("onUserJoined", payload));
                state.socket.on("user-seeked", (payload) => dispatch("onUserSeeked", payload));
            }
        },

        async reset({ state, getters, commit, dispatch }) {
            state.socket?.close?.();
            state.socket = null;

            /** @type {InterviewServerStoreGetters} */
            let { peers } = getters;
            for (const { peer } of peers) {
                try {
                    peer.destroy();
                } finally {
                    continue;
                }
            }
            state.peers = {};

            state.seekers = [];

            commit("interview/setView", { view: INTERVIEW_VIEW.JOIN.value }, { root: true });

            /** @type {InterviewServerStoreGetters} */
            let { interviewCode } = getters;
            dispatch("interview/getData", { code: interviewCode }, { root: true });
        },

        async auth({ state, getters, commit }, { seekers, isAuthorized }) {
            commit("interview/setLoading", { loading: true }, { root: true });

            seekers = seekers.filter(({ _id }) => seekers.some((seeker) => seeker._id == _id));

            /** @type {InterviewServerStoreGetters} */
            let { interviewCode } = getters;
            state.socket.emit("auth", {
                interviewCode,
                seekers,
                isAuthorized,
            });
        },

        async close({ state, getters, commit }) {
            const go = confirm("면접을 종료하시겠습니까?");
            if (!go) return;

            commit("interview/setLoading", { loading: true }, { root: true });

            /** @type {InterviewServerStoreGetters} */
            let { interviewCode } = getters;
            state.socket.emit("close", { interviewCode });
        },

        async exit({ dispatch }) {
            const go = confirm("면접에서 나가시겠습니까?");
            if (!go) return;

            await dispatch("reset");
        },

        async join({ state, getters, commit, dispatch }) {
            await dispatch("init");
            commit("interview/setLoading", { loading: true }, { root: true });

            /** @type {InterviewServerStoreGetters} */
            let { interviewCode } = getters;
            state.socket.emit("join", { interviewCode });
        },

        async seek({ state, getters, commit, dispatch }) {
            await dispatch("init");
            commit("interview/setLoading", { loading: true }, { root: true });

            /** @type {InterviewServerStoreGetters} */
            let { interviewCode } = getters;
            state.socket.emit("seek", { interviewCode });
        },

        async send({ getters }) {
            /** @type {InterviewServerStoreGetters} */
            const { peers, dataString } = getters;
            for (const { peer } of peers) {
                peer?.send?.(dataString);
            }
        },

        async onAuthed({ state, rootState, commit, dispatch }, { seekers, ...response }) {
            commit("interview/setLoading", { loading: false }, { root: true });

            if (!response.success) {
                alert(response.message);
                return;
            }

            for (const seeker of seekers) {
                commit("pullSeeker", { seeker });
            }
        },

        async onClosed({ getters, commit, dispatch }, response) {
            commit("interview/setLoading", { loading: false }, { root: true });

            if (!response.success) {
                if (response.dispatch) {
                    dispatch(response.dispatch);
                } else {
                    alert(response.message);
                }
                return;
            }

            await dispatch("reset");
            alert("면접이 종료되었습니다");
        },

        async onJoined({ state, rootState, commit, dispatch }, response) {
            commit("interview/setLoading", { loading: false }, { root: true });

            if (!response.success) {
                if (response.dispatch) {
                    dispatch(response.dispatch);
                } else {
                    alert(response.message);
                }
                return;
            }

            commit("interview/setView", { view: INTERVIEW_VIEW.ROOM.value }, { root: true });

            let { stream } = rootState.interview.client;
            const {
                data: { urls, username, password },
            } = await api.plugins.coturn.credentials.postGenerate();

            state.peerOptions = {
                initiator: true,
                trickle: true,
                stream,
                config: {
                    iceServers: [
                        {
                            urls,
                            username,
                            credential: password,
                        },
                    ],
                },
            };

            state.socket.emit("user-joined");
        },

        async onSeeked({ state, rootState, commit }, response) {
            commit("interview/setLoading", { loading: false }, { root: true });

            if (!response.success) {
                if (response.dispatch) {
                    dispatch(response.dispatch);
                } else {
                    alert(response.message);
                }
                return;
            }

            commit("interview/setView", { view: INTERVIEW_VIEW.SEEK.value }, { root: true });
        },

        async onSignal({ state, getters }, { socketId, signalData, ...props }) {
            /** @type {import("./interview-server.inits").InterviewServerState__Peer} */
            let peerItem = state.peers[socketId];
            if (!peerItem) {
                const peer = new SimplePeer({ ...state.peerOptions, initiator: false });
                peerItem = initInterviewServerState__Peer({
                    _id: props._id,
                    name: props.name,
                    username: props.username,
                    socketId,
                    peer,
                });
                state.peers[socketId] = peerItem;

                peer.on("signal", (signalData) => {
                    state.socket.emit("signal", { socketId, signalData });
                });

                peer.on("stream", (stream) => {
                    peerItem = state.peers[socketId];
                    peerItem = initInterviewServerState__Peer({
                        ...peerItem,
                        stream,
                    });
                    state.peers[socketId] = peerItem;
                });

                peer.on("data", (string) => {
                    const data = JSON.parse(string);

                    state.peers[socketId] = {
                        ...state.peers[socketId],
                        ...data,
                    };

                    state.peers = { ...state.peers };
                });

                peer.on("connect", () => {
                    /** @type {InterviewServerStoreGetters} */
                    let { dataString } = getters;
                    peer.send(dataString);
                });

                state.peers = { ...state.peers };
            }
            peerItem._id = props._id;
            peerItem.name = props.name;
            peerItem.username = props.username;
            peerItem.peer.signal(signalData);
        },

        async onRoomClosed({ getters, commit, dispatch }, { interviewCode }) {
            if (interviewCode != getters.interviewCode) return;

            commit("interview/setLoading", { loading: false }, { root: true });

            await dispatch("reset");
            alert("면접이 종료되었습니다.");
        },

        async onUserAuthed({ getters, commit, dispatch }, { interviewCode, isAuthorized }) {
            if (interviewCode != getters.interviewCode) return;

            commit("interview/setLoading", { loading: false }, { root: true });

            if (!isAuthorized) {
                alert("참여요청이 거절되었습니다");
                commit("interview/setView", { view: INTERVIEW_VIEW.JOIN.value }, { root: true });
                return;
            }

            alert("참여요청이 승인되었습니다");
            commit("interview/setView", { view: INTERVIEW_VIEW.JOIN.value }, { root: true });

            dispatch("join");
        },

        async onUserExited({ state }, socketId) {
            /** @type {import("./interview-server.inits").InterviewServerState__Peer} */
            let peerItem = state.peers[socketId];
            if (peerItem) {
                const { peer } = peerItem;
                state.alerts.push(`${peerItem.name}님이 방을 나가셨습니다.`);

                delete state.peers[socketId];
                state.peers = { ...state.peers };
                peer?.destroy?.();
            }
        },

        async onUserJoined({ state, getters }, socketId) {
            /** @type {import("./interview-server.inits").InterviewServerState__Peer} */
            let peerItem = state.peers[socketId];
            if (!peerItem) {
                const peer = new SimplePeer(state.peerOptions);
                peerItem = initInterviewServerState__Peer({
                    socketId,
                    peer,
                });
                state.peers[socketId] = peerItem;

                peer.on("signal", (signalData) => {
                    state.socket.emit("signal", { socketId, signalData });
                });

                peer.on("stream", (stream) => {
                    peerItem = state.peers[socketId];
                    peerItem = initInterviewServerState__Peer({
                        ...peerItem,
                        stream,
                    });
                    state.peers[socketId] = peerItem;
                });

                peer.on("data", (string) => {
                    const data = JSON.parse(string);

                    state.peers[socketId] = {
                        ...state.peers[socketId],
                        ...data,
                    };

                    state.peers = { ...state.peers };
                });

                peer.on("connect", () => {
                    /** @type {InterviewServerStoreGetters} */
                    let { dataString } = getters;
                    peer.send(dataString);
                    state.alerts.push(`${peerItem.name}님이 방에 참여하셨습니다.`);
                });

                state.peers = { ...state.peers };
            }
        },

        async onUserSeeked({ state, getters, commit }, { interviewCode, seeker }) {
            if (interviewCode == getters.interviewCode) {
                commit("pushSeeker", { seeker });
            }
        },
    },

    modules: {},
};

/**
 * @typedef InterviewServerStoreGetters
 * @property {string} dataString
 * @property {import("./interview-server.inits").InterviewServerState__Peer[]} peers
 * @property {string} interviewCode
 */
